"use client";

import Image from "next/image";
import { PortalEvents } from "./PortalEvents";
import { useDeferredValue, useMemo, useState, type ReactNode } from "react";
import type { BookingResource, PortalDashboard, PublicClientPortal } from "../lib/clientPortalApi";
import { availableResources, initials, safePortalImageUrl, safeWebUrl, serviceChoices, servicePrice, type ServiceChoice } from "../lib/portalPresentation";
import { matchesResourceSearch, matchesServiceSearch } from "../lib/portalSearch";
import { PortalSectionSearch } from "./PortalSectionSearch";
import { PortalLocationMap } from "./PortalLocationMap";
import { PortalContactDetails } from "./PortalContactDetails";
import { usePortalTranslations } from "../i18n/client";
import { usePortalPreferences } from "./PortalPreferences";
import styles from "./PortalSurface.module.css";

export function PortalHome({ portal, dashboard, initialSearch = "", restrictedCatalog }: { portal: PublicClientPortal; dashboard: PortalDashboard; initialSearch?: string; restrictedCatalog?: ReactNode }) {
  const t = usePortalTranslations();
  const { locale } = usePortalPreferences();
  const [serviceSearch, setServiceSearch] = useState(initialSearch);
  const deferredServiceSearch = useDeferredValue(serviceSearch);
  const serviceSearchPending = serviceSearch !== deferredServiceSearch;
  const base = `/${encodeURIComponent(portal.slug)}`;
  const presentation = portal.presentation;
  const choices = useMemo(() => serviceChoices(dashboard.bookingPortals), [dashboard.bookingPortals]);
  const hasBooking = dashboard.allowedApplicationCodes.includes("booking");
  const matchingChoices = useMemo(() => choices.filter((choice) => matchesServiceSearch(choice, deferredServiceSearch)), [choices, deferredServiceSearch]);
  const map = safeWebUrl(presentation?.mapUrl);
  const hasContact = presentation?.contactEmail || presentation?.contactPhone || presentation?.address || map;

  return <div className={styles.home} data-portal-home>
    {dashboard.linked && dashboard.allowedApplicationCodes.includes("passes") ? <section className={styles.section}><h2>{t("passes.title")}</h2><p>{t("passes.description")}</p><a className="primary-button" href={`${base}/passes`}>{t("passes.card")}</a></section> : null}
    {hasBooking || dashboard.events?.length ? <div className={styles.catalog}>
      {hasBooking ? <section className={`${styles.section} ${styles.catalogSection}`} id="services" data-portal-home-section>
      <CatalogHeading title={t("portal.navigation.services")}>
        <PortalSectionSearch label={t("portal.home.searchServices")} value={serviceSearch} onChange={setServiceSearch} resultsId="services-results" loading={serviceSearchPending} />
      </CatalogHeading>
      <div className={styles.sectionContent}>
      <SearchResultStatus query={deferredServiceSearch} count={matchingChoices.length} total={choices.length} />
      <div id="services-results" className={styles.searchResults} aria-busy={serviceSearchPending}>
      {matchingChoices.length ? <div className={styles.serviceGrid}>{matchingChoices.map((choice) => <article key={choice.key} className={styles.serviceCard}>
        <div className={styles.serviceCopy}><h3>{choice.service.name}</h3>{choice.service.description ? <p>{choice.service.description}</p> : null}{dashboard.bookingPortals.length > 1 ? <small>{choice.portalName}</small> : null}{!choice.available ? <p className={styles.unavailable}>{choice.unavailableMessage || t("portal.home.serviceUnavailable")}</p> : null}</div>
        <div className={styles.serviceDuration}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg><span>{t("portal.home.duration", {minutes: choice.service.durationMinutes})}</span></div>
        <div className={styles.serviceFooter}>
          <strong className={styles.servicePrice}>{servicePrice(choice.service, locale) ?? t("portal.home.priceOnRequest")}</strong>
          {choice.available ? <a className="primary-button" href={`${base}/reservation?${new URLSearchParams({service: choice.key})}`}>{t("portal.title.reservation")} <span aria-hidden="true">→</span></a> : <span className={styles.badge}>{t("portal.home.unavailable")}</span>}
        </div>
      </article>)}</div> : <p className={styles.empty}>{deferredServiceSearch.trim() ? t("portal.home.noMatchingServices") : t("portal.home.noServices")}</p>}
      </div>
      </div>
      </section> : null}

      <PortalEvents portal={portal} dashboard={dashboard} />
    </div> : null}
    {restrictedCatalog ? <div id={hasBooking ? undefined : "services"}>{restrictedCatalog}</div> : null}

    {presentation?.about || hasContact ? <section className={`${styles.section} ${styles.about}`} id="about" data-portal-home-section>
      {presentation?.about ? <div><h2>{t("portal.home.about")}</h2><p className={styles.multiline}>{presentation.about}</p></div> : null}
      {hasContact ? <div className={styles.contact}>
        <h2>{t("portal.home.findUs")}</h2>
        <PortalContactDetails address={presentation?.address} email={presentation?.contactEmail} phone={presentation?.contactPhone} />
      </div> : null}
      <PortalLocationMap mapUrl={map} address={presentation?.address} />
    </section> : null}
  </div>;
}

export function PortalResourcePanel({ portal, dashboard, kind }: { portal: PublicClientPortal; dashboard: PortalDashboard; kind: "employees" | "facilities" }) {
  const t = usePortalTranslations();
  const choices = useMemo(() => serviceChoices(dashboard.bookingPortals), [dashboard.bookingPortals]);
  const resources = useMemo(() => availableResources(choices).filter((resource) => kind === "employees" ? resource.type === "staff" : resource.type !== "staff"), [choices, kind]);

  return <div className={styles.home}>
    <ResourceSection key={kind} id={kind} title={t(`portal.navigation.${kind}`)} searchLabel={t(kind === "employees" ? "portal.home.searchEmployees" : "portal.home.searchFacilities")} resources={resources} choices={choices} base={`/${encodeURIComponent(portal.slug)}`} />
  </div>;
}

function ResourceSection({ id, title, searchLabel, resources, choices, base }: { id: string; title: string; searchLabel: string; resources: BookingResource[]; choices: ServiceChoice[]; base: string }) {
  const t = usePortalTranslations();
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const searchPending = query !== deferredQuery;
  const matchingResources = useMemo(() => resources.filter((resource) => matchesResourceSearch(resource, choices, deferredQuery)), [resources, choices, deferredQuery]);
  const staff = id === "employees";
  return <section className={`${styles.section} ${styles.catalogSection} ${staff ? styles.teamSection : styles.facilitiesSection}`} id={id} tabIndex={-1} aria-labelledby={`${id}-title`} data-portal-home-section>
    <CatalogHeading id={`${id}-title`} title={title}>
      <PortalSectionSearch label={searchLabel} value={query} onChange={setQuery} resultsId={`${id}-results`} loading={searchPending} />
    </CatalogHeading>
    <div className={styles.sectionContent}>
    <SearchResultStatus query={deferredQuery} count={matchingResources.length} total={resources.length} />
    <div id={`${id}-results`} className={styles.searchResults} aria-busy={searchPending}>
    {matchingResources.length ? <div className={staff ? styles.employeeGrid : styles.resourceGrid}>{matchingResources.map((resource) => <article className={staff ? styles.employeeCard : styles.resourceCard} key={resource.uuid}>
      <a className={styles.resourceProfile} href={`${base}/reservation?${new URLSearchParams({resource: resource.uuid})}`} aria-label={t("portal.home.bookResource", {name: resource.name})}>
        {safePortalImageUrl(resource.photoUrl) ? <Image className={styles.resourceImage} src={safePortalImageUrl(resource.photoUrl)!} alt="" width={staff ? 160 : 480} height={staff ? 160 : 300} unoptimized /> : <span className={styles.resourcePlaceholder} aria-hidden="true">{initials(resource.name)}</span>}
        <div className={styles.resourceCopy}><h3>{resource.name}</h3>{resource.location ? <small>{resource.location}</small> : null}{resource.description ? <p>{resource.description}</p> : null}</div>
        <span className={styles.resourceAction}>{t("portal.title.reservation")} <span aria-hidden="true">↗</span></span>
      </a>
    </article>)}</div> : <p className={styles.empty}>{t("portal.home.noMatchingResources")}</p>}
    </div>
    </div>
  </section>;
}

function CatalogHeading({ id, title, children }: { id?: string; title: string; children: ReactNode }) {
  return <div className={`${styles.sectionHeading} ${styles.catalogHeading}`}>
    <h2 id={id}>{title}</h2>
    {children}
  </div>;
}

function SearchResultStatus({ query, count, total }: { query: string; count: number; total: number }) {
  const t = usePortalTranslations();
  return query.trim() ? <p role="status" className={styles.sectionSearchStatus}>{t("portal.home.searchResults", {query: query.trim(), count, total})}</p> : null;
}
